import { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  InputBase,
  Badge,
  Snackbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Thermostat,
  Opacity,
  Co2,
  Science,
  VolumeUp,
  Air,
  FilterAlt,
  Cloud,
  Grass,
  Memory,
  Search,
  NotificationsNone,
  Menu,
  CheckBoxOutlineBlank,
  Check,
  Download,
  Close,
  EditNote,
  DescriptionOutlined,
  ShowChart,
  PlaceOutlined,
} from '@mui/icons-material';
import { DeviceNameMapper } from '../utils/DeviceNameMapper';
import { useAuth } from '../contexts/AuthContext';
import { avatarGradient, btnGradient, fontStack } from './auth/authStyles';
import ExportDataModal from './ExportDataModal';
import DeviceCurvesModal from './DeviceCurvesModal';

const C = {
  bg: '#1a1b2e',
  topbar: '#1E1E2F',
  card: '#2E2F45',
  input: '#25263B',
  border: '#3A3B55',
  textPrimary: '#FFFFFF',
  textMuted: '#A7AEC2',
  textPlaceholder: '#6D7593',
  danger: '#FF5722',
  accentGreen: '#34D399',
};

const TYPE_META = {
  climatrack: { color: '#34D399', tint: 'rgba(52,211,153,0.13)', border: 'rgba(5,150,105,0.55)' },
  aquacheck: { color: '#22D3EE', tint: 'rgba(34,211,238,0.13)', border: 'rgba(34,211,238,0.55)' },
  greenwatch: { color: '#F59E0B', tint: 'rgba(245,158,11,0.13)', border: 'rgba(245,158,11,0.55)' },
};

const ALL_TABLES = ['climatrack', 'aquacheck', 'greenwatch'];
const FILTER_OPTIONS = ['tous', ...ALL_TABLES];

const ACTIONS = [
  { id: 'notes', label: 'Notes', Icon: EditNote },
  { id: 'fiche', label: 'Fiche descriptive', Icon: DescriptionOutlined },
  { id: 'courbes', label: 'Courbes de données', Icon: ShowChart },
  { id: 'carte', label: 'Localisation', Icon: PlaceOutlined },
];

const metric = (key, label, unit, Icon) => ({ key, label, unit, Icon });

const METRICS = {
  aquacheck: [
    metric('temperature', 'Temp', '°C', Thermostat),
    metric('humidity', 'Humidité', '%', Opacity),
    metric('ground_humidity', 'Humidité sol', '%', Grass),
  ],
  climatrack: [
    metric('temperature', 'Temp', '°C', Thermostat),
    metric('humidity', 'Humidité', '%', Opacity),
    metric('co2', 'CO2', 'ppm', Co2),
    metric('tvoc', 'TVOC', 'ppb', Science),
    metric('sound_level', 'Son', 'dB', VolumeUp),
    metric('pm1_0', 'PM1.0', 'µg/m³', Air),
    metric('pm2_5', 'PM2.5', 'µg/m³', FilterAlt),
    metric('pm10', 'PM10', 'µg/m³', Cloud),
  ],
  greenwatch: [
    metric('temperature', 'Temp', '°C', Thermostat),
    metric('humidity', 'Humidité', '%', Opacity),
    metric('ground_humidity', 'Humidité sol', '%', Grass),
  ],
};

const getMetrics = (table) => METRICS[table] || METRICS.climatrack;

const hasMetricValue = (v) => {
  const n = Number(v);
  return !(v === null || v === undefined || v === '' || Number.isNaN(n));
};

const formatValue = (v) => {
  const n = Number(v);
  if (!hasMetricValue(v)) return '---';
  return Math.abs(n) >= 100 ? String(Math.round(n)) : String(Math.round(n * 10) / 10);
};

const getMetricValue = (data, key) => {
  if (!data || typeof data !== 'object') return undefined;
  return data[key] ?? data[key.toLowerCase()] ?? data[key.toUpperCase()];
};

const getVisibleKpis = (kpis) => {
  return kpis.filter((kpi) => hasMetricValue(kpi.value));
};

const keyOf = (table, id) => `${table}:${id}`;
const MOBILE_SIDE_PAD = '22px';

const DownloadPageV2 = ({
  realTimeDataByTable = {},
  stationProfiles = {},
  alertCount = 0,
  desktopLeftOffset = 280,
  onNavigateToProfile,
  onNavigateToAlerts,
  onOpenMenu,
  onOpenStationDetails,
  onOpenStationNotes,
  setSelectedDevice,
  setSelectedTable,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const [typeFilter, setTypeFilter] = useState('tous');
  const [searchQuery, setSearchQuery] = useState('');
  const [selected, setSelected] = useState({});
  const [exportOpen, setExportOpen] = useState(false);
  const [curvesDevice, setCurvesDevice] = useState(null);
  const [toast, setToast] = useState('');

  const firstName = user?.firstName || '';
  const lastName = user?.lastName || '';
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || 'U';

  const rows = useMemo(() => {
    const tables = typeFilter === 'tous' ? ALL_TABLES : [typeFilter];
    const allRows = [];

    tables.forEach((table) => {
      const tableData = realTimeDataByTable[table] || {};
      const deviceIds = Object.keys(tableData);
      const mapper = new DeviceNameMapper(table, deviceIds);
      const metrics = getMetrics(table);

      deviceIds.forEach((id) => {
        const data = tableData[id] || {};
        const profile = stationProfiles[keyOf(table, id)] || {};
        const kpis = metrics.map((m) => ({
          ...m,
          value: getMetricValue(data, m.key),
        }));

        const visibleKpis = getVisibleKpis(kpis);

        allRows.push({
          id,
          table,
          name: String(profile.name || '').trim() || mapper.getAlias(id),
          kpis,
          visibleKpis,
        });
      });
    });

    const q = searchQuery.trim().toLowerCase();
    if (!q) return allRows;
    return allRows.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.id.toLowerCase().includes(q) ||
        r.table.toLowerCase().includes(q)
    );
  }, [realTimeDataByTable, typeFilter, searchQuery, stationProfiles]);

  const selectedDevices = useMemo(
    () => rows.filter((r) => selected[keyOf(r.table, r.id)]),
    [rows, selected]
  );

  const allVisibleSelected =
    rows.length > 0 && rows.every((r) => selected[keyOf(r.table, r.id)]);

  const toggleOne = (row) => {
    const k = keyOf(row.table, row.id);
    setSelected((prev) => {
      const next = { ...prev };
      if (next[k]) delete next[k];
      else next[k] = { id: row.id, table: row.table, name: row.name };
      return next;
    });
  };

  const toggleAll = () => {
    if (allVisibleSelected) {
      setSelected((prev) => {
        const next = { ...prev };
        rows.forEach((r) => {
          delete next[keyOf(r.table, r.id)];
        });
        return next;
      });
      return;
    }
    setSelected((prev) => {
      const next = { ...prev };
      rows.forEach((r) => {
        next[keyOf(r.table, r.id)] = { id: r.id, table: r.table, name: r.name };
      });
      return next;
    });
  };

  const removeChip = (table, id) => {
    setSelected((prev) => {
      const next = { ...prev };
      delete next[keyOf(table, id)];
      return next;
    });
  };

  const handleAction = (actionId, row) => {
    setSelectedDevice?.(row.id);
    setSelectedTable?.(row.table);

    if (actionId === 'notes') {
      onOpenStationNotes?.({
        id: row.id,
        table: row.table,
        name: row.name,
      });
      return;
    }

    if (actionId === 'fiche') {
      onOpenStationDetails?.({
        id: row.id,
        table: row.table,
        name: row.name,
      });
      return;
    }

    if (actionId === 'courbes') {
      setCurvesDevice({
        id: row.id,
        table: row.table,
        name: row.name,
      });
      return;
    }

    const label =
      ACTIONS.find((a) => a.id === actionId)?.label || actionId;

    setToast(`${label} · ${row.name} (bientôt disponible)`);
  };

  const renderActions = (row) => {
    const typeMeta = TYPE_META[row.table] || TYPE_META.climatrack;
    return (
      <Box
        sx={{
          display: 'flex',
          gap: '8px',
          flexShrink: 0,
          justifyContent: { xs: 'center', md: 'flex-end' },
          width: { xs: '100%', md: 168 },
        }}
      >
        {ACTIONS.map((a) => {
          const Icon = a.Icon;
          return (
            <Box
              key={a.id}
              component="button"
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleAction(a.id, row);
              }}
              title={a.label}
              sx={{
                width: 36,
                height: 36,
                borderRadius: '10px',
                cursor: 'pointer',
                backgroundColor: C.input,
                border: `1px solid ${C.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: C.textMuted,
                transition: 'all 0.15s ease',
                '&:hover': { color: C.textPrimary, borderColor: typeMeta.color },
              }}
            >
              <Icon sx={{ fontSize: 16 }} />
            </Box>
          );
        })}
      </Box>
    );
  };

  const typePill = (table) => {
    const typeMeta = TYPE_META[table] || TYPE_META.climatrack;
    return (
      <Box
        sx={{
          display: 'inline-flex',
          borderRadius: '999px',
          px: '12px',
          py: '5px',
          flexShrink: 0,
          backgroundColor: typeMeta.tint,
          border: `1px solid ${typeMeta.border}`,
        }}
      >
        <Typography sx={{ fontSize: '12px', fontWeight: 700, color: typeMeta.color }}>
          {table}
        </Typography>
      </Box>
    );
  };

  const renderKpi = (k, compact = false) => {
    const Icon = k.Icon;
    return (
      <Box
        key={k.key}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          flex: compact ? undefined : '1 1 96px',
          minWidth: compact ? 0 : 96,
          p: compact ? '7px 8px' : '8px 10px',
          borderRadius: '10px',
          boxSizing: 'border-box',
          overflow: 'hidden',
          backgroundColor: C.input,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px', minWidth: 0 }}>
          <Icon sx={{ fontSize: compact ? 12 : 14, color: C.textMuted, flexShrink: 0 }} />
          <Typography
            sx={{
              fontSize: compact ? '9px' : '10px',
              color: C.textPlaceholder,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {k.label}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px' }}>
          <Typography sx={{ fontSize: '15px', fontWeight: 700, color: C.textPrimary, lineHeight: 1 }}>
            {formatValue(k.value)}
          </Typography>
          {hasMetricValue(k.value) && (
            <Typography sx={{ fontSize: '10px', color: C.textMuted, mb: '1px' }}>{k.unit}</Typography>
          )}
        </Box>
      </Box>
    );
  };

  const checkbox = (checked, onClick) => (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-pressed={checked}
      sx={{
        width: 20,
        height: 20,
        borderRadius: '5px',
        flexShrink: 0,
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 0,
        border: checked ? 'none' : `1.5px solid ${C.border}`,
        backgroundColor: checked ? C.accentGreen : C.input,
        color: checked ? '#0B1320' : C.textPlaceholder,
      }}
    >
      {checked && <Check sx={{ fontSize: 14, color: '#0B1320' }} />}
    </Box>
  );
  const renderNoUsableMetric = () => (
    <Box
      sx={{
        width: '100%',
        minHeight: 40,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '10px',
        backgroundColor: C.input,
        border: `1px dashed ${C.border}`,
        color: C.textPlaceholder,
        fontSize: '12px',
        fontWeight: 600,
      }}
    >
      Aucune valeur exploitable
    </Box>
  );
  const renderRow = (row) => {
    const checked = !!selected[keyOf(row.table, row.id)];
    const typeMeta = TYPE_META[row.table] || TYPE_META.climatrack;

    if (isMobile) {
      return (
        <Box
          key={keyOf(row.table, row.id)}
          onClick={() => toggleOne(row)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            p: '14px',
            borderRadius: '14px',
            backgroundColor: C.card,
            border: `1.5px solid ${checked ? C.accentGreen : C.border}`,
            boxSizing: 'border-box',
            cursor: 'pointer',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {checkbox(checked, (e) => {
              e.stopPropagation();
              toggleOne(row);
            })}
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '10px',
                backgroundColor: typeMeta.tint,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <Memory sx={{ fontSize: 18, color: typeMeta.color }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography sx={{ fontSize: '13px', fontWeight: 700, color: C.textPrimary }}>
                {row.name}
              </Typography>
              <Typography sx={{ fontSize: '11px', color: C.textPlaceholder }}>#{row.id}</Typography>
            </Box>
            {typePill(row.table)}
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: '8px',
            }}
          >
            {row.visibleKpis.length > 0
              ? row.visibleKpis.map((k) => renderKpi(k, true))
              : renderNoUsableMetric()}
          </Box>
          {renderActions(row)}
        </Box>
      );
    }

    return (
      <Box
        key={keyOf(row.table, row.id)}
        onClick={() => toggleOne(row)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          p: '16px',
          borderRadius: '14px',
          backgroundColor: C.card,
          border: `1.5px solid ${checked ? C.accentGreen : C.border}`,
          cursor: 'pointer',
          transition: 'border-color 0.15s ease',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', width: 210, flexShrink: 0 }}>
          {checkbox(checked, (e) => {
            e.stopPropagation();
            toggleOne(row);
          })}
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '10px',
              backgroundColor: typeMeta.tint,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Memory sx={{ fontSize: 18, color: typeMeta.color }} />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: 700,
                color: C.textPrimary,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {row.name}
            </Typography>
            <Typography sx={{ fontSize: '11px', color: C.textPlaceholder }}>#{row.id}</Typography>
          </Box>
        </Box>
        <Box sx={{ width: 120, flexShrink: 0 }}>{typePill(row.table)}</Box>
        <Box sx={{ flex: 1, display: 'flex', flexWrap: 'wrap', gap: '10px', minWidth: 0 }}>
          {row.visibleKpis.length > 0
            ? row.visibleKpis.map((k) => renderKpi(k))
            : renderNoUsableMetric()}
        </Box>
        {renderActions(row)}
      </Box>
    );
  };

  const menuBtnSx = {
    width: 36,
    height: 36,
    borderRadius: '10px',
    cursor: 'pointer',
    backgroundColor: 'rgba(255,255,255,0.07)',
    border: `1px solid ${C.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: C.textPrimary,
    flexShrink: 0,
  };

  const iconBtnSx = {
    width: 36,
    height: 36,
    borderRadius: '10px',
    cursor: 'pointer',
    backgroundColor: C.input,
    border: `1px solid ${C.border}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: C.textMuted,
  };

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        fontFamily: fontStack,
        boxSizing: 'border-box',
        mx: { xs: 0, md: '-30px' },
        mt: { xs: 0, md: '-30px' },
        minHeight: { xs: '100vh', md: 'auto' },
      }}
    >
      {/* Topbar */}
      <Box
        sx={{
          width: '100%',
          height: { xs: 56, md: 64 },
          px: { xs: MOBILE_SIDE_PAD, md: '28px' },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          backgroundColor: C.topbar,
          borderBottom: `1px solid ${C.border}`,
          flexWrap: 'nowrap',
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 1, minWidth: 0, overflow: 'hidden' }}>
          {isMobile && (
            <Box component="button" type="button" onClick={onOpenMenu} aria-label="Ouvrir le menu" sx={menuBtnSx}>
              <Menu sx={{ fontSize: 18 }} />
            </Box>
          )}
          <Typography
            sx={{
              fontSize: { xs: '15px', md: '18px' },
              fontWeight: 700,
              color: C.textPrimary,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              minWidth: 0,
            }}
          >
            Téléchargement
          </Typography>
          <Typography sx={{ display: { xs: 'none', sm: 'block' }, fontSize: { xs: '13px', md: '14px' }, color: C.textPlaceholder, flexShrink: 0 }}>/</Typography>
          <Typography
            sx={{
              display: { xs: 'none', sm: 'block' },
              fontSize: { xs: '12px', md: '13px' },
              color: C.textMuted,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            Export de données
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: '10px', md: '12px' }, flexShrink: 0, pr: { xs: '2px', md: 0 } }}>
          <Box
            sx={{
              display: { xs: 'none', md: 'flex' },
              alignItems: 'center',
              gap: '10px',
              width: 280,
              height: 38,
              px: '14px',
              borderRadius: '10px',
              backgroundColor: C.input,
              border: `1px solid ${C.border}`,
            }}
          >
            <Search sx={{ fontSize: 14, color: C.textPlaceholder }} />
            <InputBase
              placeholder="Rechercher un boitier..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                flex: 1,
                fontSize: '12px',
                color: C.textPrimary,
                '& input::placeholder': { color: C.textPlaceholder, opacity: 1 },
              }}
            />
          </Box>
          <Box component="button" type="button" onClick={onNavigateToAlerts} sx={iconBtnSx}>
            <Badge badgeContent={alertCount} color="error" max={99} invisible={!alertCount}>
              <NotificationsNone sx={{ fontSize: 16 }} />
            </Badge>
          </Box>
          <Box
            component="button"
            type="button"
            onClick={onNavigateToProfile}
            sx={{
              width: 36,
              height: 36,
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: avatarGradient,
              border: 'none',
              color: '#0B1320',
              fontSize: '13px',
              fontWeight: 700,
            }}
          >
            {initials}
          </Box>
        </Box>
      </Box>

      {/* Recherche mobile */}
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          alignItems: 'center',
          gap: '10px',
          mx: MOBILE_SIDE_PAD,
          mt: '14px',
          height: 44,
          px: '14px',
          borderRadius: '10px',
          backgroundColor: C.input,
          border: `1px solid ${C.border}`,
          boxSizing: 'border-box',
        }}
      >
        <Search sx={{ fontSize: 14, color: C.textPlaceholder }} />
        <InputBase
          placeholder="Rechercher un boitier..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            flex: 1,
            fontSize: '12px',
            color: C.textPrimary,
            '& input::placeholder': { color: C.textPlaceholder, opacity: 1 },
          }}
        />
      </Box>

      {/* Contenu principal */}
      <Box
        sx={{
          width: '100%',
          maxWidth: 1200,
          mx: 'auto',
          px: { xs: MOBILE_SIDE_PAD, md: 0 },
          py: { xs: '14px', md: '20px' },
          pb: selectedDevices.length > 0
            ? { xs: '180px', md: '190px' }
            : { xs: '24px', md: '20px' },
          display: 'flex',
          flexDirection: 'column',
          gap: { xs: '14px', md: '16px' },
          boxSizing: 'border-box',
          minWidth: 0,
          flex: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            gap: '12px',
          }}
        >
          <Typography sx={{ fontSize: { xs: '18px', md: '20px' }, fontWeight: 700, color: C.textPrimary }}>
            Sélection des boîtiers
          </Typography>
          <Box
            component="button"
            type="button"
            onClick={toggleAll}
            disabled={!rows.length}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              alignSelf: { xs: 'stretch', md: 'auto' },
              borderRadius: '10px',
              px: '14px',
              py: '8px',
              cursor: rows.length ? 'pointer' : 'not-allowed',
              opacity: rows.length ? 1 : 0.5,
              backgroundColor: C.input,
              border: `1px solid ${C.accentGreen}`,
              color: C.accentGreen,
              fontSize: '12px',
              fontWeight: 700,
            }}
          >
            {allVisibleSelected ? (
              <CheckBoxOutlineBlank sx={{ fontSize: 16 }} />
            ) : (
              <Check sx={{ fontSize: 16 }} />
            )}
            {allVisibleSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {FILTER_OPTIONS.map((opt) => {
            const on = typeFilter === opt;

            return (
              <Box
                key={opt}
                component="button"
                type="button"
                onClick={() => setTypeFilter(opt)}
                sx={{
                  borderRadius: '999px',
                  px: '14px',
                  py: '7px',
                  cursor: 'pointer',
                  border: `1px solid ${on ? 'rgba(52, 211, 153, 0.75)' : C.border
                    }`,
                  backgroundColor: on
                    ? 'rgba(52, 211, 153, 0.13)'
                    : C.input,
                  color: on ? '#34D399' : C.textMuted,
                  fontSize: '12px',
                  fontWeight: on ? 700 : 500,
                  transition: 'all 0.2s ease',

                  '&:hover': {
                    backgroundColor: on
                      ? 'rgba(52, 211, 153, 0.18)'
                      : 'rgba(255, 255, 255, 0.06)',
                    borderColor: on
                      ? '#34D399'
                      : 'rgba(255, 255, 255, 0.18)',
                    color: on ? '#34D399' : C.textPrimary,
                  },
                }}
              >
                {opt}
              </Box>
            );
          })}
        </Box>

        {!isMobile && (
          <Box sx={{ display: 'flex', gap: '16px', px: '16px' }}>
            <Typography sx={{ width: 210, fontSize: '11px', fontWeight: 700, letterSpacing: '0.8px', color: C.textPlaceholder }}>
              BOITIER
            </Typography>
            <Typography sx={{ width: 120, fontSize: '11px', fontWeight: 700, letterSpacing: '0.8px', color: C.textPlaceholder }}>
              TYPE
            </Typography>
            <Typography sx={{ flex: 1, fontSize: '11px', fontWeight: 700, letterSpacing: '0.8px', color: C.textPlaceholder }}>
              INDICATEURS
            </Typography>
            <Typography sx={{ width: 168, flexShrink: 0, textAlign: 'right', fontSize: '11px', fontWeight: 700, letterSpacing: '0.8px', color: C.textPlaceholder }}>
              ACTIONS
            </Typography>
          </Box>
        )}

        {rows.length === 0 ? (
          <Box sx={{ p: '40px', textAlign: 'center', borderRadius: '14px', backgroundColor: C.card, border: `1px solid ${C.border}` }}>
            <Typography sx={{ color: C.textMuted }}>
              {searchQuery ? 'Aucun boîtier ne correspond à votre recherche.' : 'Aucun boîtier disponible.'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {rows.map(renderRow)}
          </Box>
        )}
      </Box>

      {/* Barre de sélection */}
      {selectedDevices.length > 0 && (
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: { xs: 0, md: `${desktopLeftOffset}px` },
            right: 0,
            zIndex: 30,
            backgroundColor: C.topbar,
            borderTop: `1px solid ${C.border}`,
            boxSizing: 'border-box',
            px: { xs: MOBILE_SIDE_PAD, md: '28px' },
            py: { xs: '12px', md: '14px' },
            pb: { xs: 'max(12px, env(safe-area-inset-bottom))', md: '14px' },
          }}
        >
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: '8px', md: '10px' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '12px',
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: '11px', md: '12px' },
                  fontWeight: 700,
                  color: C.textPrimary,
                  flexShrink: 1,
                  minWidth: 0,
                }}
              >
                {selectedDevices.length} boîtier{selectedDevices.length > 1 ? 's' : ''} sélectionné{selectedDevices.length > 1 ? 's' : ''}
              </Typography>
              <Box
                component="button"
                type="button"
                onClick={() => setExportOpen(true)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  borderRadius: '10px',
                  px: { xs: '12px', md: '18px' },
                  py: { xs: '8px', md: '10px' },
                  width: 'auto',
                  minWidth: 'auto',
                  cursor: 'pointer',
                  border: 'none',
                  background: btnGradient,
                  color: '#0B1320',
                  fontSize: { xs: '11px', md: '12px' },
                  fontWeight: 700,
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                }}
              >
                <Download sx={{ fontSize: { xs: 13, md: 14 } }} />
                Télécharger
              </Box>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'nowrap',
                gap: '8px',
                width: '100%',
                overflowX: 'auto',
                overflowY: 'hidden',
                pb: '2px',
                WebkitOverflowScrolling: 'touch',
                '&::-webkit-scrollbar': { height: 4 },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: C.border,
                  borderRadius: 999,
                },
              }}
            >
              {selectedDevices.map((d) => {
                const meta = TYPE_META[d.table] || TYPE_META.climatrack;
                return (
                  <Box
                    key={keyOf(d.table, d.id)}
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      borderRadius: '999px',
                      px: '10px',
                      py: '6px',
                      flexShrink: 0,
                      backgroundColor: meta.tint,
                      border: `1px solid ${meta.border}`,
                    }}
                  >
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: meta.color }} />
                    <Typography sx={{ fontSize: '11px', fontWeight: 600, color: C.textPrimary, whiteSpace: 'nowrap' }}>
                      {d.name}
                    </Typography>
                    <Box
                      component="button"
                      type="button"
                      onClick={() => removeChip(d.table, d.id)}
                      aria-label={`Retirer ${d.name}`}
                      sx={{
                        display: 'flex',
                        p: 0,
                        border: 'none',
                        background: 'transparent',
                        cursor: 'pointer',
                        color: C.textMuted,
                      }}
                    >
                      <Close sx={{ fontSize: 12 }} />
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Box>
      )}

      <ExportDataModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        devices={selectedDevices}
      />

      <DeviceCurvesModal
        open={!!curvesDevice}
        onClose={() => setCurvesDevice(null)}
        device={curvesDevice}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={2500}
        onClose={() => setToast('')}
        message={toast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default DownloadPageV2;
